"use client"
import React from "react";
import { Heading } from "@/components/ui/typography";
type Props = {
  heading?: string;
  youDoHeading?: string;
  mutetaxesHeading?: string;
};

export default function OffShoreSection({
  heading = "Why most incorporation agencies don't handle offshore claims",
  youDoHeading = "You What You Do",
  mutetaxesHeading = "What Mutetaxes Do",
}: Props = {}) {
  return (
    <section className="bg-black w-full overflow-hidden py-[60px] px-4">
      <div className="max-w-[1200px] mx-auto flex flex-col gap-[50px]">
        {/* HEADER */}
        <div className="flex flex-col items-center text-center max-w-[714px] mx-auto gap-6">
          <Heading as="h2" variant="sectionCentered">
            {heading}
          </Heading>

          <p className="text-[16px] leading-[24px] text-white/70 font-medium font-inter">
            Many providers help with company formation only. They file the
            Certificate of Incorporation, Business Registration Certificate, and
            Articles of Association — then stop.
            <br />
            But formation alone does not determine tax treatment. Your offshore
            profits exemption application determines whether your company
            qualifies for 0% taxation.
          </p>
        </div>

        {/* TWO COLUMNS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* YOU DO */}
          <div className="flex flex-col gap-4">
            <div className="bg-[#0f1218] rounded-2xl p-6 sm:p-8 flex flex-col gap-6">
              <Heading as="h3" variant="cardTitleLight">
                {youDoHeading}
              </Heading>

              <div className="flex flex-col gap-3">
                {youDo.map((item, i) => (
                  <div
                    key={i}
                    className="bg-white/5 rounded-lg px-4 py-3 flex items-center gap-3"
                  >
                    <span>
                      <svg
                        width="31"
                        height="31"
                        viewBox="0 0 31 31"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15.4823 29.5555C17.3309 29.5579 19.1617 29.195 20.8695 28.4875C22.5774 27.7801 24.1286 26.7421 25.434 25.4332C26.7429 24.1278 27.7809 22.5766 28.4883 20.8687C29.1958 19.1609 29.5587 17.3301 29.5563 15.4815C29.5587 13.6329 29.1957 11.8021 28.4883 10.0942C27.7808 8.38638 26.7429 6.83516 25.434 5.52971C24.1286 4.22084 22.5774 3.18286 20.8695 2.47542C19.1617 1.76798 17.3309 1.40503 15.4823 1.40742C13.6337 1.40506 11.8029 1.76804 10.095 2.47547C8.38717 3.18291 6.83595 4.22088 5.53051 5.52971C4.22167 6.83516 3.18371 8.38638 2.47627 10.0942C1.76883 11.8021 1.40586 13.6329 1.40821 15.4815C1.40582 17.3301 1.76877 19.1609 2.47621 20.8687C3.18366 22.5766 4.22164 24.1278 5.53051 25.4332C6.83595 26.7421 8.38717 27.78 10.095 28.4875C11.8029 29.1949 13.6337 29.5579 15.4823 29.5555Z"
                          fill="#00A63E"
                        />
                        <path
                          d="M9.85265 15.4815L14.0749 19.7037L22.5193 11.2593"
                          fill="#00A63E"
                        />
                        <path
                          d="M9.85265 15.4815L14.0749 19.7037L22.5193 11.2593M15.4823 29.5555C17.3309 29.5579 19.1617 29.195 20.8695 28.4875C22.5774 27.7801 24.1286 26.7421 25.434 25.4332C26.7429 24.1278 27.7809 22.5766 28.4883 20.8687C29.1958 19.1609 29.5587 17.3301 29.5563 15.4815C29.5587 13.6329 29.1957 11.8021 28.4883 10.0942C27.7808 8.38638 26.7429 6.83516 25.434 5.52971C24.1286 4.22084 22.5774 3.18286 20.8695 2.47542C19.1617 1.76798 17.3309 1.40503 15.4823 1.40742C13.6337 1.40506 11.8029 1.76804 10.095 2.47547C8.38717 3.18291 6.83595 4.22088 5.53051 5.52971C4.22167 6.83516 3.18371 8.38638 2.47627 10.0942C1.76883 11.8021 1.40586 13.6329 1.40821 15.4815C1.40582 17.3301 1.76877 19.1609 2.47621 20.8687C3.18366 22.5766 4.22164 24.1278 5.53051 25.4332C6.83595 26.7421 8.38717 27.78 10.095 28.4875C11.8029 29.1949 13.6337 29.5579 15.4823 29.5555Z"
                          stroke="white"
                          strokeWidth="2.81481"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <p className="text-[18px] text-white font-inter">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            {/* BOTTOM CARD SECTION */}
            <div className="grid lg:grid-cols-1 gap-6">
              {/* LEFT CARD */}
              <div className="bg-[#0f1218] rounded-2xl shadow-xl p-6 flex flex-col gap-4 border border-white/10">
                <div className="space-y-2">
                  <Heading as="h3" variant="cardTitleLight">
                    How Much Work Is Setting Up an HK Company Personally?
                  </Heading>

                  <p className="text-white/70 text-[14px]">
                    Very little. Most founders spend less than 60 minutes total
                    across the entire onboarding process.
                  </p>
                </div>

                <div className="space-y-4">
                  {/* YOUR EFFORT */}
                  <div className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                    <span className="text-sm font-medium">Your effort</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold bg-white/10 p-[8px] rounded-lg">
                        ~5%
                      </span>
                      <div className="relative w-[152px] h-[7px]">
                        {/* Background bar */}
                        <div className="w-full h-[7px] rounded-[9px] bg-white/10 border border-transparent" />

                        {/* Progress fill */}
                        <div className="absolute left-[1px] top-[1px] w-[14px] h-[5px] rounded-[9px] bg-[#00A63E]" />
                      </div>
                    </div>
                  </div>

                  {/* MUTE TAXES */}
                  <div className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                    <span className="text-sm font-medium">
                      MuteTaxes handles
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold bg-white/10 p-[8px] rounded-lg">
                        ~95%
                      </span>
                      <div className="w-[152px] h-[7px] bg-white/10 rounded-[9px] overflow-hidden">
                        <div
                          className="h-full bg-[#00A63E] rounded-[9px] transition-all duration-300"
                          style={{ width: "95%" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-4">
                  {faq.map((item, i) => (
                    <div
                      key={i}
                      className="bg-[#0f1218] border-l-2 border-[#A9C2DA] rounded-lg px-4 py-3"
                    >
                      <p className="text-[12px] italic text-white/60">
                        {item.q}
                      </p>
                      <p className="text-[14px] text-white/70 font-medium mt-1">
                        {item.a}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* RIGHT INFO BOXES */}
            </div>
          </div>

          {/* MUTETAXES DO */}
          <div className="bg-[#335CFF] rounded-2xl px-4 py-4 sm:px-[24px] sm:py-[32px] flex flex-col gap-6">
            <Heading as="h3" variant="cardTitleLight">
              {mutetaxesHeading}
            </Heading>

            <div className="flex flex-col gap-3">
              {muteTaxes.map((item, i) => (
                <div
                  key={i}
                  className="bg-[#476CFF] rounded-lg px-4 py-3.5 flex items-center gap-3"
                >
                  <span>
                    <svg
                      width="31"
                      height="31"
                      viewBox="0 0 31 31"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15.4823 29.5555C17.3309 29.5579 19.1617 29.195 20.8695 28.4875C22.5774 27.7801 24.1286 26.7421 25.434 25.4332C26.7429 24.1278 27.7809 22.5766 28.4883 20.8687C29.1958 19.1609 29.5587 17.3301 29.5563 15.4815C29.5587 13.6329 29.1957 11.8021 28.4883 10.0942C27.7808 8.38638 26.7429 6.83516 25.434 5.52971C24.1286 4.22084 22.5774 3.18286 20.8695 2.47542C19.1617 1.76798 17.3309 1.40503 15.4823 1.40742C13.6337 1.40506 11.8029 1.76804 10.095 2.47547C8.38717 3.18291 6.83595 4.22088 5.53051 5.52971C4.22167 6.83516 3.18371 8.38638 2.47627 10.0942C1.76883 11.8021 1.40586 13.6329 1.40821 15.4815C1.40582 17.3301 1.76877 19.1609 2.47621 20.8687C3.18366 22.5766 4.22164 24.1278 5.53051 25.4332C6.83595 26.7421 8.38717 27.78 10.095 28.4875C11.8029 29.1949 13.6337 29.5579 15.4823 29.5555Z"
                        fill="#00A63E"
                      />
                      <path
                        d="M9.85265 15.4815L14.0749 19.7037L22.5193 11.2593"
                        fill="#00A63E"
                      />
                      <path
                        d="M9.85265 15.4815L14.0749 19.7037L22.5193 11.2593M15.4823 29.5555C17.3309 29.5579 19.1617 29.195 20.8695 28.4875C22.5774 27.7801 24.1286 26.7421 25.434 25.4332C26.7429 24.1278 27.7809 22.5766 28.4883 20.8687C29.1958 19.1609 29.5587 17.3301 29.5563 15.4815C29.5587 13.6329 29.1957 11.8021 28.4883 10.0942C27.7808 8.38638 26.7429 6.83516 25.434 5.52971C24.1286 4.22084 22.5774 3.18286 20.8695 2.47542C19.1617 1.76798 17.3309 1.40503 15.4823 1.40742C13.6337 1.40506 11.8029 1.76804 10.095 2.47547C8.38717 3.18291 6.83595 4.22088 5.53051 5.52971C4.22167 6.83516 3.18371 8.38638 2.47627 10.0942C1.76883 11.8021 1.40586 13.6329 1.40821 15.4815C1.40582 17.3301 1.76877 19.1609 2.47621 20.8687C3.18366 22.5766 4.22164 24.1278 5.53051 25.4332C6.83595 26.7421 8.38717 27.78 10.095 28.4875C11.8029 29.1949 13.6337 29.5579 15.4823 29.5555Z"
                        stroke="white"
                        strokeWidth="2.81481"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <p className="text-[18px] text-white font-inter">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- DATA ARRAYS ---------------- */

const youDo = [
  "Attend one strategy call",
  "Send passport copy",
  "Send proof of address",
  "Confirm business activity summary",
  "Review and sign formation documents",
];

const muteTaxes = [
  "Structure eligibility review",
  "Company incorporation filing",
  "Companies Registry coordination",
  "Registered address setup",
  "Company secretary activation",
  "Bank selection guidance",
  "Bank application preparation",
  "Payment processor readiness positioning",
  "Compliance timeline activation",
  "Offshore eligibility preparation support",
  "IRD positioning preparation coordination",
];

const faq = [
  {
    q: "do I need to do anything to set up HK company",
    a: "Just identity verification documents and one short call.",
  },
  {
    q: "what documents do I need for HK company",
    a: "Everything else is handled inside the setup workflow.",
  },
];
